import {
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { find, isNil, map } from "lodash";
import { useMemo, useState } from "react";
import {
	DeviceStatus,
	DeviceStatusWithDisplayDto,
} from "../../../models/Device";
import { DeviceStatusSelectorProps } from "./types";
import { derivedDeviceStatuses } from "../../../utils/deviceStatus";

export default function DeviceStatusSelector(props: DeviceStatusSelectorProps) {
	const { reactOnChange } = props;

	const [selectedStatus, setSelectedStatus] = useState<
		| DeviceStatusWithDisplayDto
		| {
				status: string;
		  }
	>({
		status: "all",
	});

	const deviceStatusesWithDisplayName = useMemo(
		() => derivedDeviceStatuses,
		[],
	);

	return (
		<Box sx={{ width: "100%" }}>
			<FormControl
				fullWidth
				size='small'
				variant='outlined'
				sx={{
					"& .MuiInputLabel-root": {
						color: "rgba(15, 23, 42, 0.62)",
						fontSize: 12,
						fontWeight: 700,
					},
					"& .MuiOutlinedInput-root": {
						borderRadius: 2,
						backgroundColor: "rgba(248, 250, 252, 0.96)",
						"& fieldset": { borderColor: "rgba(15, 23, 42, 0.14)" },
						"&:hover fieldset": { borderColor: "rgba(2, 132, 199, 0.45)" },
						"&.Mui-focused fieldset": {
							borderColor: "#0284c7",
							borderWidth: 1,
						},
					},
					"& .MuiSelect-select": {
						display: "flex",
						alignItems: "center",
						gap: 1,
						py: 1.25,
						fontWeight: 700,
						color: "#172033",
					},
				}}>
				<InputLabel id='device-status-select-label'>Status</InputLabel>
				<Select
					labelId='device-status-select-label'
					id='device-status-select'
					value={selectedStatus ? selectedStatus.status : "all"}
					label='Status'
					defaultValue=''
					renderValue={(selected) => {
						const option = find(
							deviceStatusesWithDisplayName,
							(deviceStatusWithDisplay: DeviceStatusWithDisplayDto) =>
								deviceStatusWithDisplay.status === selected,
						);
						return (
							<Box
								component='span'
								sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								{selected === DeviceStatus.ONLINE ? (
									<CheckCircleIcon sx={{ color: "#16a34a", fontSize: 18 }} />
								) : selected === DeviceStatus.ERROR ? (
									<ErrorIcon sx={{ color: "#dc2626", fontSize: 18 }} />
								) : selected === DeviceStatus.OFFLINE ? (
									<ErrorIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
								) : (
									<RadioButtonUncheckedIcon
										sx={{ color: "#0284c7", fontSize: 18 }}
									/>
								)}
								{option?.displayName || "Todos los status"}
							</Box>
						);
					}}
					MenuProps={{
						PaperProps: {
							sx: {
								mt: 0.75,
								borderRadius: 2,
								boxShadow: "0 18px 44px rgba(15, 23, 42, 0.16)",
								"& .MuiMenuItem-root": {
									mx: 0.75,
									my: 0.35,
									borderRadius: 1.5,
									fontWeight: 600,
								},
							},
						},
					}}
					onChange={(e: SelectChangeEvent) => {
						let statusWithDisplayMatch = find(
							deviceStatusesWithDisplayName,
							(deviceStatusWithDisplay: DeviceStatusWithDisplayDto) =>
								deviceStatusWithDisplay.status === e.target.value
						);

						if (isNil(statusWithDisplayMatch)) {
							statusWithDisplayMatch = {
								status: DeviceStatus.ALL,
								displayName: "Todos los status",
							};
						}
						setSelectedStatus(statusWithDisplayMatch);
						reactOnChange(statusWithDisplayMatch);
					}}>
					<MenuItem value={"all"}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<RadioButtonUncheckedIcon sx={{ color: "#0284c7", fontSize: 18 }} />
							<Typography variant='body2' sx={{ fontWeight: 700 }}>
								Todos los status
							</Typography>
						</Box>
					</MenuItem>
					{map(
						deviceStatusesWithDisplayName,
						(deviceStatusWithDisplay: DeviceStatusWithDisplayDto) => {
							return (
								<MenuItem
									key={deviceStatusWithDisplay.status}
									value={deviceStatusWithDisplay.status}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										{deviceStatusWithDisplay.status === DeviceStatus.ONLINE ? (
											<CheckCircleIcon
												sx={{ color: "#16a34a", fontSize: 18 }}
											/>
										) : deviceStatusWithDisplay.status === DeviceStatus.ERROR ? (
											<ErrorIcon sx={{ color: "#dc2626", fontSize: 18 }} />
										) : (
											<ErrorIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
										)}
										<Typography variant='body2' sx={{ fontWeight: 700 }}>
											{deviceStatusWithDisplay.displayName}
										</Typography>
									</Box>
								</MenuItem>
							);
						}
					)}
				</Select>
			</FormControl>
		</Box>
	);
}
